import React, { useEffect, useMemo, useState } from 'react';
import { useParams, NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useCatalogSettings, useTheme } from '../../App';
import BackButton from '../../components/common/BackButton/BackButton';
import { apiClient } from '../../services/apiClient';
import { getCategories } from '../../services/settingsCache';

export default function ProductCategory() {
  const { category } = useParams();
  const { isNightMode } = useTheme();
  const { showProductRate } = useCatalogSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const slug = (category || '').toLowerCase();

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  const selectedBrand = searchParams.get('brand') || 'all';

  const categoryMatch = categories.find((item) => item.slug === slug);
  const isSubcategory = categoryMatch?.parentCategory;
  const parentCategory = isSubcategory ? (typeof categoryMatch.parentCategory === "object" ? categoryMatch.parentCategory : categories.find((item) => item._id === categoryMatch.parentCategory)) : null;
  
  const title = categoryMatch?.name || (slug ? 'Products' : 'Products');

  useEffect(() => {
    if (slug && !categoryMatch && /^[a-f0-9]{24}$/i.test(slug)) {
      navigate(`/product/${encodeURIComponent(slug)}`, { replace: true });
      return;
    }

    if (slug && !categoryMatch) {
      setItems([]);
      setError('');
    }
  }, [categoryMatch, navigate, slug]);

  useEffect(() => {
    let cancelled = false;

    const loadFilters = async () => {
      try {
        setFiltersLoading(true);
        const [categoriesResponse, brandsResponse] = await Promise.all([
          getCategories(),
          apiClient.get('/brands', { unwrap: true }),
        ]);

        if (cancelled) return;

        // Handle category response format {success: true, data: categories}
        const categoriesData = categoriesResponse?.success && categoriesResponse?.data 
          ? categoriesResponse.data 
          : (Array.isArray(categoriesResponse) ? categoriesResponse : []);
        
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        setBrands(Array.isArray(brandsResponse) ? brandsResponse : []);
      } catch (err) {
        console.error('Failed to load category/brand filters', err);
      } finally {
        if (!cancelled) {
          setFiltersLoading(false);
        }
      }
    };

    loadFilters();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!slug) {
      setItems([]);
      return;
    }

    let cancelled = false;

    const fetchCategoryProducts = async () => {
      try {
        setIsLoading(true);
        setError('');

        const query = new URLSearchParams({ categorySlug: slug, sort: 'latest' });
        if (selectedBrand !== 'all') query.set('brand', selectedBrand);

        const data = await apiClient.get(`/products?${query.toString()}`);
        if (cancelled) return;

        setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load products for category', err);
        if (!cancelled) {
          setError('Unable to load products from server.');
          setItems([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchCategoryProducts();

    return () => {
      cancelled = true;
    };
  }, [selectedBrand, slug]);

  const brandOptions = useMemo(
    () => {
      if (!slug) return brands;
      const filterSlug = isSubcategory && parentCategory ? parentCategory.slug : slug;
      return brands.filter((item) => item.categorySlug === filterSlug);
    },
    [brands, slug, isSubcategory, parentCategory],
  );

  return (
    <main
      style={{
        padding: 20,
        fontFamily: 'Arial, sans-serif',
        background: isNightMode ? '#020617' : '#fff',
        minHeight: '100vh',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <nav
          style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
          aria-label="Breadcrumb"
        >
          <BackButton fallbackTo="/products" />

          <div>
            <NavLink to="/" style={{ marginRight: 8 }}> Home
            </NavLink>
            <span aria-hidden>{'>'}</span>
            <NavLink to="/products" style={{ margin: '0 8px' }}>
              Catalog
            </NavLink>
            {isSubcategory && parentCategory && (
              <>
                <span aria-hidden>{'>'}</span>
                <NavLink to={`/products/${parentCategory.slug}`} style={{ margin: '0 8px' }}>
                  {parentCategory.name}
                </NavLink>
              </>
            )}
            <span aria-hidden>{'>'}</span>
            <span style={{ marginLeft: 8 }}>{title}</span>
          </div>
        </nav>

        <h1 style={{ marginTop: 0 }}>{title}</h1>
        {error && <p style={{ color: '#dc2626' }}>{error}</p>}

        {(brandOptions.length > 0 || filtersLoading) && (
          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}
            aria-label="Subcategory brands"
          >
            <button
              type="button"
              onClick={() => setSearchParams(new URLSearchParams())}
              style={{
                padding: '6px 12px',
                borderRadius: 999,
                border: '1px solid #e5e7eb',
                background: selectedBrand === 'all' ? '#a7e08d' : '#ffffff',
                color: '#111827',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              All Brands
            </button>
            {brandOptions.map((brand) => {
              const isActive = selectedBrand.toLowerCase() === brand.name.toLowerCase();
              return (
                <button
                  key={brand.name}
                  type="button"
                  onClick={() => setSearchParams(new URLSearchParams({ brand: brand.name }))}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 999,
                    border: '1px solid #e5e7eb',
                    background: isActive ? '#a7e08d' : '#ffffff',
                    color: '#111827',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {brand.name}
                </button>
              );
            })}
            {brandOptions.length === 0 && filtersLoading && (
              <span style={{ fontSize: 12, color: '#6b7280' }}>Loading brands...</span>
            )}
          </div>
        )}

        {items.length === 0 && !isLoading ? (
          <div style={{ 
            padding: 40, 
            textAlign: 'center',
            color: isNightMode ? '#cbd5e1' : '#6b7280'
          }}>
            <h3 style={{ marginBottom: 12, color: isNightMode ? '#fff' : '#111827' }}>
              No products found in this category
            </h3>
            <p>
              {title} currently has no products available. 
              Please check back later or browse other categories.
            </p>
            <NavLink
              to="/products"
              style={{
                display: 'inline-block',
                marginTop: 16,
                padding: '8px 16px',
                borderRadius: 8,
                border: '1px solid #5d7f4e',
                background: 'transparent',
                color: isNightMode ? '#bbf7d0' : '#166534',
                textDecoration: 'none',
              }}
            >
              Browse All Products
            </NavLink>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
            {isLoading
              ? Array.from({ length: 8 }).map((_, index) => (
                  <div
                    key={`category-skeleton-${index}`}
                    style={{
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: isNightMode ? '#0f172a' : '#fff',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div style={{ width: '100%', aspectRatio: '4/3', background: '#e5e7eb' }} />
                    <div style={{ padding: 12, display: 'grid', gap: 8 }}>
                      <div style={{ height: 14, width: '70%', background: '#e5e7eb' }} />
                      <div style={{ height: 12, width: '40%', background: '#e5e7eb' }} />
                    </div>
                  </div>
                ))
              : items.map((product) => (
                  <article
                    key={product._id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/product/${product._id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(`/product/${product._id}`);
                      }
                    }}
                    style={{
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: isNightMode ? '#0f172a' : '#fff',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                    }}
                  >
                    {product.imageUrl && (
                      <div
                        style={{
                          width: '100%',
                          aspectRatio: '4/3',
                          overflow: 'hidden',
                          backgroundColor: isNightMode ? '#020617' : '#f3f4f6',
                        }}
                      >
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          loading="lazy"
                          decoding="async"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      </div>
                    )}
                    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', flex: 1, gap: 6 }}>
                      <h3 style={{ margin: 0 }}>{product.name}</h3>
                      {product.brand ? (
                        <div style={{ fontSize: 12, color: '#6b7280', minHeight: 18 }}>{product.brand}</div>
                      ) : (
                        <div style={{ fontSize: 12, color: 'transparent', minHeight: 18, userSelect: 'none' }}>&nbsp;</div>
                      )}
                      <div
                        style={{
                          fontSize: 11,
                          color: product.inStock !== false ? '#15803d' : '#dc2626',
                        }}
                      >
                        {product.inStock !== false ? 'In stock' : 'Out of stock'}
                      </div>
                      <div style={{ marginBottom: 4, color: '#16a34a', fontWeight: 600, minHeight: 24 }}>
                        {showProductRate && typeof product.price === 'number'
                          ? `Rs. ${product.price.toFixed(2)}`
                          : 'Contact for Price'}
                      </div>
                      <div style={{ marginTop: 'auto', paddingTop: 8, width: '100%' }}>
                        <NavLink
                          to={`/product/${product._id}`}
                          style={{
                            width: '100%',
                            padding: '9px 12px',
                            borderRadius: 8,
                            border: '1px solid #5d7f4e',
                            background: 'transparent',
                            color: isNightMode ? '#bbf7d0' : '#166534',
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            fontSize: 13,
                            fontWeight: 600,
                            boxSizing: 'border-box',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {showProductRate ? 'View Product' : 'Get Best Price'}
                        </NavLink>
                      </div>
                    </div>
                  </article>
                ))}
          </div>
        )}
      </div>
    </main>
  );
}
